import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hasPlanAccess, isAdminRole } from '../utils/planAccess';
import { canUseAskCanira } from '../utils/askCanira';
import { useSidebar } from '../contexts/SidebarContext';
import api from '../utils/api';
import './DashboardSidebar.css';
import { readPref, writePref } from '../utils/userPrefs';
import { videoMinutesDisplay } from '../utils/videoMinutes';
import { useToast } from './ui/Toast';

/** Minimum gap between usage-meter refreshes. See the usage effect below. */
const USAGE_REFRESH_MS = 30_000;

/**
 * The actual sidebar UI. Rendered exactly once, by <AppShell/>, outside
 * <Routes> — so it persists across navigation. Don't render this from a page;
 * render the default-exported slot below instead.
 */
export const DashboardSidebarView = () => {
  const { currentUser, getSubscriptionStatus, logout } = useAuth();
  const location = useLocation();
  const { sidebarOpen, closeSidebar, sidebarCollapsed, toggleCollapsed, getSidebarScroll, setSidebarScroll } = useSidebar();
  const asideRef = useRef(null);

  // Restore sidebar scroll position on mount (survives route changes)
  useEffect(() => {
    if (asideRef.current) {
      asideRef.current.scrollTop = getSidebarScroll();
    }
  }, []);

  // Save scroll position as user scrolls
  const handleSidebarScroll = useCallback((e) => {
    setSidebarScroll(e.target.scrollTop);
  }, [setSidebarScroll]);

  // Collapsible section states - persist in localStorage
  const [expandedSections, setExpandedSections] = useState(() => {
    const defaults = {
      main: true,
      podcasts: true,
      courses: true,
      marketing: true,
      aiTools: true,
      automation: false,
      settings: false,
      enterprise: false,
      admin: false
    };
    const saved = readPref('sidebarSections');
    // Merge saved with defaults so new sections (e.g. podcasts) are visible
    // even for users with cached localStorage from before the section existed
    return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
  });

  const isActive = (path) => location.pathname === path;

  // Progressive disclosure. A new host got nine links on day one; these four
  // appear once there's something behind them (activityCounts from /me —
  // backend services/activityCounts.js). A missing key (an older cached user)
  // shows the link, and the page you're on always shows its own link.
  const counts = currentUser?.activityCounts || {};
  const has = (key) => counts[key] === undefined || counts[key] === true || Number(counts[key]) > 0;
  const onPath = (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`);
  // "In use" (from activity alone) vs "shown" (also while you're on the page).
  const usesPodcasts = has('podcasts');
  const usesClips = has('clips');
  // The calendar shows sessions as well as scheduled posts.
  const usesCalendar = has('scheduledPosts') || Number(counts.webinars) > 0;
  // Contacts once the first person signs up (or the host works in a team,
  // whose contacts they may see).
  const usesContacts = has('hasContacts') || !!currentUser?.teamPlan || isAdminRole(currentUser?.role);
  const showPodcasts = usesPodcasts || onPath('/podcasts');
  const showClips = usesClips || onPath('/my-clips');
  const showCalendar = usesCalendar || onPath('/dashboard/calendar');
  const showContacts = usesContacts || onPath('/dashboard/contacts');

  // Hidden links weren't discoverable: a new host had no way to know they
  // exist. "N more appear as you go" lists them with what brings each one,
  // and a link that arrives gets a "New" dot plus a one-time note.
  const UNLOCKABLE = [
    { key: 'contacts', to: '/dashboard/contacts', label: 'Contacts', uses: usesContacts,
      hint: 'Appears after your first sign-up',
      intro: 'Contacts is now in your sidebar. Everyone who signs up for your sessions, joins a course, buys or follows you lands here.' },
    { key: 'clips', to: '/my-clips', label: 'My Clips', uses: usesClips,
      hint: 'Appears after you make your first clip',
      intro: 'My Clips is now in your sidebar. Every clip you make is kept here.' },
    { key: 'podcasts', to: '/podcasts', label: 'Podcasts', uses: usesPodcasts,
      hint: 'Appears after you create a podcast',
      intro: 'Podcasts is now in your sidebar. Your shows and episodes live here.' },
    { key: 'calendar', to: '/dashboard/calendar', label: 'Calendar', uses: usesCalendar,
      hint: 'Appears after you schedule your first session',
      intro: 'Calendar is now in your sidebar. Your sessions and scheduled posts, by date.' },
  ];
  const upcoming = UNLOCKABLE.filter((u) => !u.uses && !onPath(u.to));
  const [showUpcoming, setShowUpcoming] = useState(false);
  const toast = useToast();
  const readList = (key) => {
    try { const v = JSON.parse(readPref(key) || '[]'); return Array.isArray(v) ? v : []; } catch (_) { return []; }
  };
  const [newLinks, setNewLinks] = useState(() => readList('sidebarNewLinks'));
  // Only once the server has sent the new counts (an older cached user shows
  // every link, which isn't an "arrival").
  const countsKnown = 'hasContacts' in counts;
  const hiddenNow = UNLOCKABLE.filter((u) => !u.uses).map((u) => u.key);
  const hiddenKey = hiddenNow.join(',');
  useEffect(() => {
    if (!currentUser || !countsKnown) return;
    const before = readList('sidebarHiddenLinks');
    const arrived = before.filter((k) => !hiddenNow.includes(k));
    if (arrived.length) {
      const next = [...new Set([...readList('sidebarNewLinks'), ...arrived])];
      writePref('sidebarNewLinks', JSON.stringify(next));
      setNewLinks(next);
      arrived.forEach((k) => {
        const item = UNLOCKABLE.find((u) => u.key === k);
        if (item && toast?.info) toast.info(item.intro, { title: `New: ${item.label}` });
      });
    }
    writePref('sidebarHiddenLinks', JSON.stringify(hiddenNow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, countsKnown, hiddenKey]);
  // The dot clears the first time you open the page.
  useEffect(() => {
    const opened = UNLOCKABLE.filter((u) => newLinks.includes(u.key) && onPath(u.to)).map((u) => u.key);
    if (!opened.length) return;
    const next = newLinks.filter((k) => !opened.includes(k));
    writePref('sidebarNewLinks', JSON.stringify(next));
    setNewLinks(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  const NewDot = ({ k }) => (newLinks.includes(k) ? <span className="sidebar-new-dot">New</span> : null);

  // White-label surfaces (branded Roku channel). Studio (`business`) is the
  // entry white-label tier, so this is business-and-above rather than the
  // previous enterprise-only check — that one hid the link from Studio
  // customers who do have a branded channel.
  //
  // Goes through the shared helper rather than a local tier table. The local
  // one omitted `scale` (a real paid tier ABOVE business), so a Scale customer
  // ranked as free and lost this link — the same class of bug this gate was
  // widened to fix, one tier up. The helper also covers super_admin and the
  // trial uplift, which the local table missed.
  const subscriptionStatus = getSubscriptionStatus ? getSubscriptionStatus() : {};
  const canWhiteLabel = hasPlanAccess(currentUser, subscriptionStatus, 'business');

  /*
   * Usage meters (storage / AI clips / AI images).
   *
   * These are quota displays, so staleness is the failure that matters: they
   * used to refresh only because the sidebar remounted on every navigation.
   * Now that it mounts once per session, refetching purely on [currentUser]
   * would freeze them — a host who uploads a recording would watch
   * MediaLibrary's own storage number update while this meter, inches away,
   * kept the old value for the rest of the session.
   *
   * So: refresh on navigation again, but throttled. Navigation is a good proxy
   * for "something may have changed" (you upload, then you move on), and the
   * throttle keeps this from re-creating the per-page request storm the app
   * shell exists to remove — clicking through five pages costs one refresh,
   * not five. Both endpoints share one timer since they're refreshed together.
   */
  const [storageData, setStorageData] = useState(null);
  const videoTime = videoMinutesDisplay(storageData);
  const [imageUsage, setImageUsage] = useState(null);
  const lastUsageFetchRef = useRef(0);

  useEffect(() => {
    if (!currentUser) return;
    const now = Date.now();
    // Always fetch on first run (ref starts at 0); afterwards at most every 30s.
    if (lastUsageFetchRef.current && now - lastUsageFetchRef.current < USAGE_REFRESH_MS) return;
    lastUsageFetchRef.current = now;

    let cancelled = false;
    api.get('/media-library/storage')
      .then(res => { if (!cancelled) setStorageData(res.data?.storage || res.data); })
      .catch(() => {});
    api.get('/ai/image-usage')
      .then(res => { if (!cancelled) setImageUsage(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser, location.pathname]);

  // "Your week": shown to a host who can use it or already has weeks, with
  // the number of posts waiting (they're dropped if not approved in time).
  // Refreshed on navigation, throttled like the meters, and always when you
  // leave or open a week, since that's where the number changes.
  const [weekInfo, setWeekInfo] = useState(null);
  const lastWeekFetchRef = useRef(0);
  const onWeekPage = location.pathname.startsWith('/dashboard/week');
  useEffect(() => {
    if (!currentUser) return;
    const now = Date.now();
    if (!onWeekPage && lastWeekFetchRef.current && now - lastWeekFetchRef.current < USAGE_REFRESH_MS) return;
    lastWeekFetchRef.current = now;
    let cancelled = false;
    api.get('/your-week/waiting', { suppressGlobalError: true })
      .then((res) => { if (!cancelled) setWeekInfo(res.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, location.pathname]);
  const showYourWeek = !!weekInfo?.show || onWeekPage;
  const weekWaiting = Number(weekInfo?.count) > 0 ? Number(weekInfo.count) : 0;

  // Usage meters: one row, opened on demand (four bars of numbers was a lot
  // for a host with nothing stored yet) — or by itself when anything is 80%+.
  const pct = (used, cap) => (cap > 0 ? (Number(used) || 0) / cap * 100 : 0);
  const usageNearLimit = [
    pct(imageUsage?.clipsThisMonth, imageUsage?.clipLimit),
    Number(storageData?.percentUsed?.total) || 0,
    Number(videoTime?.percent) || 0,
    pct(imageUsage?.proUsed, imageUsage?.proCap),
  ].some((p) => p >= 80);
  const [usageExpanded, setUsageExpanded] = useState(() => readPref('sidebarUsageOpen') === '1');
  const usageOpen = usageExpanded || usageNearLimit;
  const toggleUsage = () => {
    const next = !usageOpen;
    setUsageExpanded(next);
    writePref('sidebarUsageOpen', next ? '1' : '0');
  };
  // Nothing to upgrade to on the top plans, or as an admin.
  const showUpgrade = !isAdminRole(currentUser?.role) && !hasPlanAccess(currentUser, subscriptionStatus, 'scale');

  const handleLinkClick = () => {
    // Only close sidebar on mobile — on desktop, closing triggers re-render
    // which resets scroll position back to the top
    if (window.innerWidth <= 768) {
      closeSidebar();
    }
  };

  const toggleSection = (section) => {
    const newState = {
      ...expandedSections,
      [section]: !expandedSections[section]
    };
    setExpandedSections(newState);
    writePref('sidebarSections', JSON.stringify(newState));
  };

  // SVG icons for section headers (replaces emoji for enterprise look)
  const sectionIcons = {
    podcasts: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
        <line x1="12" y1="19" x2="12" y2="23"></line>
        <line x1="8" y1="23" x2="16" y2="23"></line>
      </svg>
    ),
    marketing: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
      </svg>
    ),
    aiTools: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
      </svg>
    ),
    automation: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="13 17 18 12 13 7"></polyline>
        <polyline points="6 17 11 12 6 7"></polyline>
      </svg>
    ),
    settings: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"></circle>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15 1.65 1.65 0 0 0 3.17 14H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68 1.65 1.65 0 0 0 10 3.17V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.32 9c.3.56.82.96 1.43 1.07H21a2 2 0 0 1 0 4h-.09"></path>
      </svg>
    ),
    enterprise: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
      </svg>
    ),
    admin: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      </svg>
    ),
  };

  // Collapsible Section Header Component
  const SectionHeader = ({ id, label, icon, isExpanded }) => (
    <button
      className={`sidebar-section-header ${isExpanded ? 'expanded' : ''}`}
      onClick={() => toggleSection(id)}
      data-tooltip={label}
    >
      <span className="section-header-content">
        <span className="section-icon">{sectionIcons[id] || icon}</span>
        <span className="section-label">{label}</span>
      </span>
      <svg
        className={`section-chevron ${isExpanded ? 'expanded' : ''}`}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );

  // Plan upgrade badge for locked sidebar items
  const PlanBadge = ({ plan }) => {
    const labels = { starter: 'BUILD', professional: 'SERVE', business: 'STUDIO', enterprise: 'ENT' };
    return <span className="sidebar-badge upgrade">{labels[plan] || plan.toUpperCase()}</span>;
  };

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

      <aside ref={asideRef} onScroll={handleSidebarScroll} className={`dashboard-sidebar ${sidebarOpen ? 'mobile-open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Collapse Toggle Button */}
        <button
          className="sidebar-collapse-toggle"
          onClick={toggleCollapsed}
          title={sidebarCollapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse sidebar (Ctrl+B)'}
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`collapse-icon ${sidebarCollapsed ? 'collapsed' : ''}`}
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>


        {/* ===================================================================
            Simplified Navigation — reduced from ~25 items across 8 collapsible
            sections down to 5 always-visible items (+ conditional Admin).
            Every route the old sidebar linked to is still registered in
            App.js; the items below just replace navigation with Home (which
            unifies all content) + Grow / Settings hub landing pages.
            =================================================================== */}
        <div className="sidebar-nav sidebar-main-nav">
          <NavLink
            to="/dashboard"
            className={`sidebar-link ${isActive('/dashboard') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Home"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </span>
            <span className="sidebar-label">Home</span>
          </NavLink>

          {/* Contacts and Canira University were previously hidden from
              trial users via `hasPlan('starter')`. That hurt discovery and
              conversion — new users couldn't even SEE the features. The
              sidebar now shows both links to everyone; any Pro-only
              content inside each page should paywall at the content
              level, not hide the nav entirely. */}
          {showContacts && (
          <NavLink
            to="/dashboard/contacts"
            className={`sidebar-link ${isActive('/dashboard/contacts') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Contacts"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </span>
            <span className="sidebar-label">Contacts</span>
            <NewDot k="contacts" />
          </NavLink>
          )}

          <NavLink
            to="/dashboard/media"
            className={`sidebar-link ${isActive('/dashboard/media') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Media Library"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                <line x1="12" y1="11" x2="12" y2="17"></line>
                <line x1="9" y1="14" x2="15" y2="14"></line>
              </svg>
            </span>
            <span className="sidebar-label">Media Library</span>
          </NavLink>

          {showClips && (
          <NavLink
            to="/my-clips"
            className={`sidebar-link ${isActive('/my-clips') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="My Clips"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="6" cy="6" r="3"></circle>
                <circle cx="6" cy="18" r="3"></circle>
                <line x1="20" y1="4" x2="8.12" y2="15.88"></line>
                <line x1="14.47" y1="14.48" x2="20" y2="20"></line>
                <line x1="8.12" y1="8.12" x2="12" y2="12"></line>
              </svg>
            </span>
            <span className="sidebar-label">My Clips</span>
            <NewDot k="clips" />
          </NavLink>
          )}

          {showPodcasts && (
          <NavLink
            to="/podcasts"
            className={`sidebar-link ${location.pathname.startsWith('/podcasts') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Podcasts"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
            </span>
            <span className="sidebar-label">Podcasts</span>
            <NewDot k="podcasts" />
          </NavLink>
          )}


          <NavLink
            to="/grow"
            className={`sidebar-link ${isActive('/grow') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Marketing"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
              </svg>
            </span>
            <span className="sidebar-label">Marketing</span>
          </NavLink>

          {/* Content Calendar / Publishing Scheduler. The page self-gates with a
              friendly "not enabled yet" message when CONTENT_SCHEDULER_ENABLED
              is off, so this link is safe to show; it's the only entry point
              users have to reach the scheduler. */}
          {showCalendar && (
          <NavLink
            to="/dashboard/calendar"
            className={`sidebar-link ${isActive('/dashboard/calendar') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Calendar"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </span>
            <span className="sidebar-label">Calendar</span>
            <NewDot k="calendar" />
          </NavLink>
          )}

          {showYourWeek && (
          <NavLink
            to="/dashboard/week"
            className={`sidebar-link ${onWeekPage ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip={weekWaiting ? `Your week (${weekWaiting} waiting)` : 'Your week'}
            aria-label={weekWaiting ? `Your week, ${weekWaiting} ${weekWaiting === 1 ? 'post' : 'posts'} waiting` : undefined}
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 3l1.9 5.8H20l-4.9 3.6 1.9 5.8L12 14.6l-5 3.6 1.9-5.8L4 8.8h6.1z"></path>
              </svg>
              {weekWaiting > 0 && <span className="sidebar-count-dot" aria-hidden="true" />}
            </span>
            <span className="sidebar-label">Your week</span>
            {weekWaiting > 0 && <span className="sidebar-badge count">{weekWaiting > 99 ? '99+' : weekWaiting}</span>}
          </NavLink>
          )}

          {/* Premieres moved to the Marketing hub (/grow).
              Scheduling a prerecorded video out to Facebook is the same job as
              scheduling a social post, so it belongs beside the other
              publishing tools rather than competing for a top-level nav slot.
              The route still works and the page still self-gates on
              FACEBOOK_PREMIERE_ENABLED — this only changes where it's found. */}

          {/* TV Channel (white-label Roku) — admins + white-label plans.
              Gated on Studio (business) and above, NOT enterprise-only: Studio
              is the entry white-label tier, so an enterprise-only check hid the
              link from exactly the customers who have a branded channel. */}
          {canWhiteLabel && (
            <NavLink
              to="/dashboard/tv-channel"
              className={`sidebar-link ${isActive('/dashboard/tv-channel') ? 'active' : ''}`}
              onClick={handleLinkClick}
              data-tooltip="TV Channel"
            >
              <span className="sidebar-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="13" rx="2" ry="2"></rect>
                  <polyline points="17 2 12 7 7 2"></polyline>
                </svg>
              </span>
              <span className="sidebar-label">TV Channel</span>
            </NavLink>
          )}

          <NavLink
            to="/settings"
            className={`sidebar-link ${isActive('/settings') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Settings"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </span>
            <span className="sidebar-label">Settings</span>
          </NavLink>

          {canUseAskCanira(currentUser) && (
          <NavLink
            to="/dashboard/ask"
            className={`sidebar-link ${isActive('/dashboard/ask') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Ask Canira"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </span>
            <span className="sidebar-label">Ask Canira</span>
          </NavLink>
          )}

          {isAdminRole(currentUser?.role) && (
          <NavLink
            to="/admin-hub"
            className={`sidebar-link ${isActive('/admin-hub') || location.pathname.startsWith('/admin') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Admin"
          >
            <span className="sidebar-icon admin">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </span>
            <span className="sidebar-label">Admin</span>
          </NavLink>
          )}
        </div>


        {/* Canira University, out of the main list: there for new hosts, not
            competing with the tools they use every day. */}
        <div className="sidebar-nav sidebar-secondary-nav">
          {upcoming.length > 0 && (
            <>
              <button
                type="button"
                className={`sidebar-upcoming-toggle ${showUpcoming ? 'open' : ''}`}
                onClick={() => setShowUpcoming((v) => !v)}
                aria-expanded={showUpcoming}
                aria-controls="sidebar-upcoming"
              >
                {upcoming.length} more appear as you go
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </button>
              {showUpcoming && (
                <div id="sidebar-upcoming" className="sidebar-upcoming-list">
                  {upcoming.map((u) => (
                    <NavLink key={u.key} to={u.to} className="sidebar-link sidebar-link--upcoming" onClick={handleLinkClick}>
                      <span className="sidebar-label">
                        {u.label}
                        <small className="sidebar-upcoming-hint">{u.hint}</small>
                      </span>
                    </NavLink>
                  ))}
                </div>
              )}
            </>
          )}
          <NavLink
            to="/university"
            className={`sidebar-link ${location.pathname.startsWith('/university') ? 'active' : ''}`}
            onClick={handleLinkClick}
            data-tooltip="Learn: Canira University"
          >
            <span className="sidebar-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                <path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5"></path>
              </svg>
            </span>
            <span className="sidebar-label">Learn</span>
          </NavLink>
        </div>

        <div className="sidebar-footer">
          <button
            type="button"
            className={`sidebar-footer-title sidebar-usage-toggle ${usageOpen ? 'open' : ''}`}
            onClick={toggleUsage}
            aria-expanded={usageOpen}
            aria-controls="sidebar-usage-meters"
          >
            <span>Usage this month{usageNearLimit ? ' — nearly full' : ''}</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {usageOpen && (
          <div id="sidebar-usage-meters">
          <div className="sidebar-usage">
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                </span>
                <span className="usage-label">AI Clips</span>
              </div>
              <div className="usage-bar-container">
                <div
                  className="usage-bar"
                  style={{ width: `${(imageUsage?.clipLimit && imageUsage.clipLimit > 0) ? Math.min((imageUsage.clipsThisMonth / imageUsage.clipLimit) * 100, 100) : 0}%` }}
                ></div>
              </div>
              <span className="usage-value">
                {imageUsage?.clipLimit > 0
                  ? `${imageUsage.clipsThisMonth || 0} / ${imageUsage.clipLimit}`
                  : `${imageUsage?.clipsThisMonth || 0} this month`}
              </span>
            </div>
          </div>
          <div className="sidebar-usage">
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </span>
                <span className="usage-label">Storage</span>
              </div>
              <div className="usage-bar-container">
                <div className="usage-bar" style={{ width: `${Math.min(storageData?.percentUsed?.total || 0, 100)}%` }}></div>
              </div>
              <span className="usage-value">{storageData?.formatted?.totalUsedHours || '0 hrs'} / {storageData?.formatted?.totalLimitHours || '1 hrs'}</span>
            </div>
          </div>
          {videoTime && (
            <div className="sidebar-usage">
              {/* Stored video time: the allowance a host actually runs out of.
                  Cloudflare Stream bills by duration, so this can fill before
                  the GB bar above does. */}
              <div className="usage-item" title="Minutes of video stored on your account, including captioned copies of clips">
                <div className="usage-header">
                  <span className="usage-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="23 7 16 12 23 17 23 7"></polygon>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                    </svg>
                  </span>
                  <span className="usage-label">Video time</span>
                </div>
                <div className="usage-bar-container">
                  <div
                    className={`usage-bar usage-bar--${videoTime.tone}`}
                    style={{ width: `${videoTime.percent}%` }}
                  ></div>
                </div>
                <span className="usage-value">{videoTime.usedLabel} / {videoTime.limitLabel}</span>
              </div>
            </div>
          )}
          <div className="sidebar-usage">
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </span>
                <span className="usage-label">AI Images</span>
              </div>
              <div className="usage-bar-container">
                <div
                  className="usage-bar"
                  style={{ width: `${imageUsage?.proCap ? Math.min((imageUsage.proUsed / imageUsage.proCap) * 100, 100) : 0}%` }}
                ></div>
              </div>
              <span className="usage-value">
                {imageUsage?.proCap != null
                  ? `${imageUsage.proUsed || 0} / ${imageUsage.proCap} Pro`
                  : `${imageUsage?.proUsed || 0} Pro`}
              </span>
            </div>
          </div>
          </div>
          )}
          {showUpgrade && (
          <NavLink to="/pricing" className="upgrade-link" onClick={handleLinkClick} data-tooltip="Upgrade your plan">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="13 17 18 12 13 7"></polyline>
              <polyline points="6 17 11 12 6 7"></polyline>
            </svg>
            Upgrade your plan
          </NavLink>
          )}
        </div>

        {/* Logout Button */}
        <button
          className="sidebar-logout-btn"
          onClick={() => { closeSidebar(); logout(); }}
          data-tooltip="Logout"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          <span className="sidebar-logout-label">Logout</span>
        </button>
      </aside>
    </>
  );
};

/**
 * Sidebar slot — what pages render. Emits no DOM of its own; it only tells the
 * shell "this page wants the sidebar". The sidebar itself lives in <AppShell/>
 * and is never unmounted by navigation, so its three startup fetches
 * (team invitations, media storage, AI image usage) run once per session
 * rather than once per page view.
 *
 * Kept as the default export so the ~42 pages that already do
 * `import DashboardSidebar from '../components/DashboardSidebar'` keep working
 * untouched. A few pass `key={location.key}`; that now just re-runs this
 * no-op effect, which is harmless.
 */
const DashboardSidebarSlot = () => {
  const { requestSidebar } = useSidebar();
  useEffect(() => requestSidebar(), [requestSidebar]);
  return null;
};

export default DashboardSidebarSlot;
